import {
    Directive,
    HostListener,
    ElementRef,
    Renderer2,
    forwardRef,
    Input, OnInit
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NG_VALIDATORS,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {LocalizedDatePipe} from "@pipes";

const DATE_INPUT_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateInputDirective),
    multi: true
};

const DATE_INPUT_VALIDATOR: any = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => DateInputDirective),
    multi: true
};


@Directive({
    selector: '[appDateInput]',
    providers: [DATE_INPUT_VALUE_ACCESSOR, DATE_INPUT_VALIDATOR]
})
export class DateInputDirective implements ControlValueAccessor, OnInit {
    private el: HTMLInputElement;
    private FORMAT: string;
    private SEPARATOR: string;

    @Input() minDate?: Date;
    @Input() maxDate?: Date;

    private onChange: (val: any) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private transloco: TranslocoService,
        private localizedDatePipe: LocalizedDatePipe
    ) {
        this.el = this.elementRef.nativeElement;
        this.FORMAT = this.transloco.getActiveLang() === 'en' ? 'MM/DD/YYYY' : 'DD.MM.YYYY';
        this.SEPARATOR = this.transloco.getActiveLang() === 'en' ? '/' : '.';
    }

    ngOnInit() {
        this.renderer.setAttribute(this.el, 'placeholder', this.FORMAT);
    }

    @HostListener('input', ['$event'])
    onInput(event: InputEvent): void {
        const value: string = this.el.value.replace(new RegExp(`[^0-9${this.escapedSeparator()}]`, 'g'), '')
            .replace(new RegExp(`[${this.escapedSeparator()}]{2,}`, 'g'), this.SEPARATOR);

        const isEn = this.transloco.getActiveLang() === 'en';
        const formatted = isEn ? this.formatEnDate(value, event) : this.formatLocalDate(value, event);

        if (this.el.value !== formatted) {
            this.renderer.setProperty(this.el, 'value', formatted);
        }

        const parsed = this.parseDate(formatted);
        this.onTouched();
        this.onChange(this.isWithinRange(parsed) ? parsed : undefined);
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
    }

    private formatEnDate(value: string, event: InputEvent): string {
        const parts = value.split(this.SEPARATOR);
        const month = (parts[0] || '').slice(0, 2);
        const day = (parts[1] || '').slice(0, 2);
        const year = (parts[2] || '').slice(0, 4);

        let formatted = month;
        const isDeleting = event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward';

        if (month.length === 2 && parts.length === 1 && !isDeleting) formatted += this.SEPARATOR;
        if (parts.length > 1 || day) formatted += day.length > 0 ? this.SEPARATOR + day : this.SEPARATOR;
        if (day.length === 2 && parts.length === 2 && !isDeleting) formatted += this.SEPARATOR;
        if (parts.length > 2 || year) formatted += year.length > 0 ? this.SEPARATOR + year : this.SEPARATOR;

        return formatted;
    }

    private formatLocalDate(value: string, event: InputEvent): string {
        const parts = value.split(this.SEPARATOR);
        const day = (parts[0] || '').slice(0, 2);
        const month = (parts[1] || '').slice(0, 2);
        const year = (parts[2] || '').slice(0, 4);

        let formatted = day;
        const isDeleting = event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward';

        if (day.length === 2 && parts.length === 1 && !isDeleting) formatted += this.SEPARATOR;
        if (parts.length > 1 || month) formatted += month.length > 0 ? this.SEPARATOR + month : this.SEPARATOR;
        if (month.length === 2 && parts.length === 2 && !isDeleting) formatted += this.SEPARATOR;
        if (parts.length > 2 || year) formatted += year.length > 0 ? this.SEPARATOR + year : this.SEPARATOR;

        return formatted;
    }


    writeValue(value: any): void {
        if (value === undefined || value === null || value === '') {
            this.renderer.setProperty(this.el, 'value', '');
            return;
        }

        if (this.isNgbDateStruct(value)) {
            const date = new Date(value.year, value.month - 1, value.day);
            this.renderer.setProperty(this.el, 'value', this.formatDate(date));
            return;
        }

        if (value instanceof Date && !isNaN(value.getTime())) {
            this.renderer.setProperty(this.el, 'value', this.formatDate(value));
            return;
        }

        if (typeof value === 'string') {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                const parsed = this.parseDate(value);
                if (parsed) {
                    this.renderer.setProperty(this.el, 'value', this.formatDate(parsed));
                    return;
                }
            }
        }

        this.renderer.setProperty(this.el, 'value', String(value));
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.renderer.setProperty(this.el, 'disabled', isDisabled);
    }

    private parseDate(value: string): Date | undefined {
        if (!value) return undefined;
        const parts = value.split(this.SEPARATOR).map(p => p.trim());
        if (parts.length !== 3) return undefined;

        const nums = parts.map(p => parseInt(p, 10));
        if (this.FORMAT.startsWith('MM')) {
            const [month, day, year] = nums;
            if (!month || !day || !year) return undefined;
            const d = new Date(year, month - 1, day);
            return (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) ? d : undefined;
        } else {
            const [day, month, year] = nums;
            if (!day || !month || !year) return undefined;
            const d = new Date(year, month - 1, day);
            return (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) ? d : undefined;
        }
    }

    private formatDate(date: Date): string {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return this.FORMAT.startsWith('MM')
            ? `${mm}${this.SEPARATOR}${dd}${this.SEPARATOR}${yyyy}`
            : `${dd}${this.SEPARATOR}${mm}${this.SEPARATOR}${yyyy}`;
    }

    private isNgbDateStruct(v: any): v is NgbDateStruct {
        return v && typeof v === 'object' && 'year' in v && 'month' in v && 'day' in v;
    }

    private escapedSeparator(): string {
        return this.SEPARATOR === '.' ? '\\.' : this.SEPARATOR;
    }

    private isWithinRange(date: Date | null | undefined): boolean {
        if (!date) return false;
        if (this.minDate && date.getTime() < this.minDate.getTime()) return false;
        if (this.maxDate && date.getTime() > this.maxDate.getTime()) return false;
        return true;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const value: string = this.el.value;
        if (!value) return null;

        const parsed = this.parseDate(value);
        if (!parsed) {
            return { invalidDate: true };
        }
        if (this.minDate) {
            if (parsed.getTime() < this.minDate.getTime()) {
                return {
                    min: this.localizedDatePipe.transform(this.minDate, 'dd.MM.yyyy')
            }
            }
        }
        if (this.maxDate) {
            if (parsed.getTime() > this.maxDate.getTime()) {
                return {max: this.localizedDatePipe.transform(this.maxDate, 'dd.MM.yyyy')}
            }
        }
        return null;
    }
}
